import { useEffect, lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/Header';
import Footer from './components/Footer';
import CartDrawer from './components/CartDrawer';
import PreorderDialog from './components/PreorderDialog';
import Home from './pages/Home';
// Non-Home pages are lazy-loaded so the homepage initial JS bundle stays
// trim. Each page becomes its own chunk; users only download what they hit.
const Catalog               = lazy(() => import('./pages/Catalog'));
const Collections           = lazy(() => import('./pages/Collections'));
const FAQ                   = lazy(() => import('./pages/FAQ'));
const ShoppingWithUs        = lazy(() => import('./pages/ShoppingWithUs'));
const InteriorStyling       = lazy(() => import('./pages/InteriorStyling'));
const Showrooms             = lazy(() => import('./pages/Showrooms'));
const ReferAFriend          = lazy(() => import('./pages/ReferAFriend'));
const Customisation         = lazy(() => import('./pages/Customisation'));
const Sustainability        = lazy(() => import('./pages/Sustainability'));
const Ambassador            = lazy(() => import('./pages/Ambassador'));
const Affiliate             = lazy(() => import('./pages/Affiliate'));
const Admin                 = lazy(() => import('./pages/Admin'));
const ProductDetail         = lazy(() => import('./pages/ProductDetail'));
const PreorderProductDetail = lazy(() => import('./pages/PreorderProductDetail'));
const About                 = lazy(() => import('./pages/About'));
const Contact               = lazy(() => import('./pages/Contact'));
const Journal               = lazy(() => import('./pages/Journal'));
const CheckoutSuccess       = lazy(() => import('./pages/CheckoutSuccess'));
const CheckoutCancel        = lazy(() => import('./pages/CheckoutCancel'));
const AfterpayReturn        = lazy(() => import('./pages/AfterpayReturn'));
const Checkout              = lazy(() => import('./pages/Checkout'));
const Wishlist              = lazy(() => import('./pages/Wishlist'));
import { CartProvider } from './context/CartContext';
import { PreorderProvider } from './context/PreorderContext';
import { WishlistProvider } from './context/WishlistContext';
import { SiteCopyProvider } from './context/SiteCopyContext';
import { Toaster } from './components/ui/toaster';
import ConciergeChat from './components/ConciergeChat';

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Defer until DOM has mounted the new route's content
      const id = hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [pathname, hash]);
  return null;
};

// Lazy-routed Routes wrapped in a single Suspense fallback. Each non-Home
// page becomes its own chunk so the initial JS bundle stays trim.
const AppRoutes = () => (
  <Suspense fallback={
    <div className="min-h-[60vh] flex items-center justify-center text-[#A07A4F] text-[11px] tracking-[0.32em] uppercase">
      Loading…
    </div>
  }>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/collections" element={<Collections />} />
      <Route path="/collections/all" element={<Catalog />} />
      <Route path="/collections/:slug" element={<Catalog />} />
      <Route path="/collections/:slug/:sub" element={<Catalog />} />
      <Route path="/catalog" element={<Catalog />} />
      <Route path="/products/preorder/:id" element={<PreorderProductDetail />} />
      <Route path="/products/:id" element={<ProductDetail />} />
      <Route path="/about" element={<About />} />
      <Route path="/journal" element={<Journal />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/checkout/success" element={<CheckoutSuccess />} />
      <Route path="/checkout/cancel" element={<CheckoutCancel />} />
      <Route path="/checkout/afterpay/return" element={<AfterpayReturn />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/shopping-with-us" element={<ShoppingWithUs />} />
      <Route path="/interior-styling" element={<InteriorStyling />} />
      <Route path="/showrooms" element={<Showrooms />} />
      <Route path="/refer-a-friend" element={<ReferAFriend />} />
      <Route path="/customisation" element={<Customisation />} />
      <Route path="/sustainability" element={<Sustainability />} />
      <Route path="/ambassador-program" element={<Ambassador />} />
      <Route path="/affiliate-program" element={<Affiliate />} />
      <Route path="/blog" element={<Navigate to="/journal" replace />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/wishlist" element={<Wishlist />} />
    </Routes>
  </Suspense>
);

function App() {
  return (
    <div className="App bg-[#F5EDDD]">
      <HelmetProvider>
        <BrowserRouter>
          <CartProvider>
            <PreorderProvider>
              <WishlistProvider>
              <SiteCopyProvider>
              <ScrollToTop />
              <Header />
              <CartDrawer />
              <PreorderDialog />
              <AppRoutes />
              <Footer />
              <Toaster />
              <ConciergeChat />
              </SiteCopyProvider>
              </WishlistProvider>
            </PreorderProvider>
          </CartProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
